import * as React from "react";
import { useContext } from "react";
import {
    ClinicianCredential,
    ExperienceLevelType,
    IExperienceLevel,
    IExperienceLevelOption,
} from "intake_shared/src/types";
import store from "../../../../store";
import { useToasts } from "react-toast-notifications";
import { AclContext } from "../../../../contexts/AclContext";
import Loader from "../../bits/Loader/Loader";
import styles from "./ManageExperienceLevels.module.scss";
import { Button } from "../../bits/Button/Button";
import { SearchInput } from "../../bits/SearchInput/SearchInput";
import { Badge } from "../../bits/Badge/Badge";
import { Accordion } from "../../bits/Accordion/Accordion";
import { Dropdown } from "../../bits/Dropdown/Dropdown";
import MoreIcon from "../../icons/More";
import { AlertDialog } from "../../bits/AlertDialog/AlertDialog";
import { Dialog } from "../../bits/Dialog/Dialog";
import { toTitleCase } from "../../../../utils/utils";
import { Input } from "../../bits/Input/Input";
import { Select } from "../../bits/Select/Select";
import { FormCheckbox } from "../../bits/FormCheckbox/FormCheckbox";
import { httpsCallable } from "firebase/functions";
import { fxs } from "../../../../utils/firestore";

const COLORS = ["#4f5c28", "#63d709", "#b34411", "#666666", "#e66447", "#6c757d"];

type Option = {
    label: string;
    value: string;
};

export default function ManageExperienceLevels() {
    const { addToast } = useToasts();
    const userAcl = useContext(AclContext)?.authUser?.acl;
    const [loading, setLoading] = React.useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = React.useState<boolean>(false);
    const [showNotifyDialog, setShowNotifyDialog] = React.useState(false);
    const [filteredLevels, setFilteredLevels] = React.useState(
        [] as IExperienceLevelOption[]
    );
    const [showAddExperienceLevelDialog, setShowAddExperienceLevelDialog] =
        React.useState<boolean>(false);
    const [selectedExperienceLevel, setSelectedExperienceLevel] =
        React.useState<IExperienceLevelOption | null>(null);
    const [clinicianExperienceLevels, setClinicianExperienceLevels] = React.useState(
        {
            levels: [] as IExperienceLevelOption[],
            clinician_levels: {} as {
                [key: string]: {
                    email: string;
                    level_details: IExperienceLevel;
                    locations: string[];
                }[];
            },
            clinician_count: 0,
        }
    );

    const [categories, setCategories] = React.useState([] as string[]);
    const fetchExperienceLevels = async () => {
        try {
            const levels = await store.getAllExperienceLevels();
            const { matched_clinicians: clinician_levels, clinician_count } =
                await store.getExperienceLevelAcrossClinicians();

            const categoryList = Array.from(
                new Set(levels.map((data) => data.category))
            );

            setCategories(categoryList);
            setFilteredLevels(levels);
            setClinicianExperienceLevels({
                levels,
                clinician_levels,
                clinician_count,
            });
        } catch (ex) {
            console.error(ex);
        }
    };

    const handleDeleteExperienceLevel = async (
        experienceLevelToDelete: IExperienceLevelOption
    ) => {
        setLoading(true);
        try {
            const result = await store.deleteExperienceLevels(
                experienceLevelToDelete
            );
            result && fetchExperienceLevels();
            setLoading(false);
            setShowDeleteDialog(false);
            setSelectedExperienceLevel(null);
        } catch (ex) {
            console.error(ex);
        }
    };

    const handleNotifyAllClinicians = async () => {
        setLoading(true);
        setShowNotifyDialog(false);
        try {
            const notifyFn = httpsCallable<unknown, { count: number }>(
                fxs,
                "httpFunctionNotifyAllCliniciansExperienceLevels"
            );
            const result = await notifyFn({});
            addToast(`Notification queued for ${result.data.count} clinicians`, {
                appearance: "success",
            });
        } catch (ex) {
            console.error(ex);
            addToast("Error sending notifications", { appearance: "error" });
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        fetchExperienceLevels();
    }, []);

    const categorySet = new Set();
    const categoryColors: { [key: string]: string } = {};

    const [newExp, setNewExp] = React.useState({
        name: "",
        category: "",
        optionType: ExperienceLevelType.LOWHIGH,
        credential: "",
        clinicianHidden: false,
        certification_toogle: false,
    });

    const categoriesListOptions = [
        ...categories.map((data, index) => ({
            label: data,
            value: data,
        })),
        ...(categories.includes("Provider Style")
            ? []
            : [
                  {
                      label: "Provider Style",
                      value: "Provider Style",
                  },
              ]),
    ];

    const optionTypeListOptions = [
        { label: "Low to High", value: ExperienceLevelType.LOWHIGH },
        { label: "Yes or No", value: ExperienceLevelType.YESNO },
        { label: "Conversational or Fluent", value: ExperienceLevelType.CONVFLUENT },
    ];

    const credentialListOptions = [
        { label: "All", value: "ALL" },
        ...Object.keys(ClinicianCredential).map((k: string) => ({
            label: (ClinicianCredential as any)[k],
            value: (ClinicianCredential as any)[k],
        })),
    ];

    return (
        <div className="v2-page">
            <Loader
                show={loading}
                loadingText="Please wait while we process your request. This may take a moment."
            />
            <AlertDialog
                open={showNotifyDialog}
                onClose={() => setShowNotifyDialog(false)}
                onSaveClick={handleNotifyAllClinicians}
                title="Notify All Clinicians"
                description="This will send an email to all clinicians asking them to update their experience levels. Are you sure you want to continue?"
                saveText="Send"
                onCancelClick={() => setShowNotifyDialog(false)}
            />
            <AlertDialog
                open={showDeleteDialog && Boolean(selectedExperienceLevel)}
                onClose={() => setShowDeleteDialog(false)}
                onSaveClick={() =>
                    handleDeleteExperienceLevel(
                        selectedExperienceLevel as IExperienceLevelOption
                    )
                }
                isDeleting
                title="Delete Experience Level"
                description={`Are you sure you want to delete experience level?`}
                saveText="Delete"
                onCancelClick={() => setShowDeleteDialog(false)}
            />
            <Dialog
                open={showAddExperienceLevelDialog}
                title="New Experience Level "
                onCancel={() => setShowAddExperienceLevelDialog(false)}
                onClose={() => setShowAddExperienceLevelDialog(false)}
                submitBtnFormValue="add-experience-level-form"
                saveText="Add new experience"
            >
                <form
                    id="add-experience-level-form"
                    className="v2-form"
                    onSubmit={async (e) => {
                        e.preventDefault();
                        try {
                            await store.addNewExperienceLevel(
                                toTitleCase(newExp.category),
                                newExp.optionType as ExperienceLevelType,
                                toTitleCase(newExp.name),
                                newExp.credential,
                                newExp.clinicianHidden,
                                newExp.certification_toogle
                            );
                            await fetchExperienceLevels();
                            addToast("Experience level added", {
                                appearance: "success",
                            });
                            setShowAddExperienceLevelDialog(false);
                        } catch (e) {
                            addToast("Error occured", {
                                appearance: "error",
                            });
                        }
                    }}
                >
                    <Input
                        isRequired
                        label="Name"
                        name="name"
                        placeholder="Enter name"
                        onChange={(e) => {
                            setNewExp({
                                ...newExp,
                                name: e.target.value,
                            });
                        }}
                        value={newExp.name}
                    />
                    <Select
                        isRequired
                        label="Category"
                        name="category"
                        options={categoriesListOptions}
                        onChange={(val) => {
                            setNewExp({
                                ...newExp,
                                category: (val as Option).value,
                            });
                        }}
                        value={categoriesListOptions.find(
                            (opt) => opt.value === newExp.category
                        )}
                        isLongListInDialog
                    />
                    <Select
                        isRequired
                        label="Option Type"
                        name="optionType"
                        options={optionTypeListOptions}
                        onChange={(val) => {
                            setNewExp({
                                ...newExp,
                                optionType: (val as Option)
                                    .value as ExperienceLevelType,
                            });
                        }}
                        value={optionTypeListOptions.find(
                            (opt) => opt.value === newExp.optionType
                        )}
                        isLongListInDialog
                    />
                    <Select
                        isRequired
                        label="Credential"
                        name="credential"
                        placeholder="Enter credential"
                        options={credentialListOptions}
                        onChange={(val) => {
                            setNewExp({
                                ...newExp,
                                credential: (val as Option).value,
                            });
                        }}
                        value={credentialListOptions.find(
                            (opt) => opt.value === newExp.credential
                        )}
                        isLongListInDialog
                    />
                    <div className="gridTwo">
                        <FormCheckbox
                            label="Certification toggle"
                            checked={newExp.certification_toogle}
                            onChange={(e) => {
                                setNewExp({
                                    ...newExp,
                                    certification_toogle: e.target.checked,
                                });
                            }}
                        />
                        <FormCheckbox
                            label="Hide from clinicians"
                            checked={newExp.clinicianHidden}
                            onChange={(e) => {
                                setNewExp({
                                    ...newExp,
                                    clinicianHidden: e.target.checked,
                                });
                            }}
                        />
                    </div>
                </form>
            </Dialog>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.headerTop}>
                        <SearchInput
                            placeholder="Search experience levels"
                            onChange={(e) => {
                                const searchLabel = e.target.value;
                                if (searchLabel.length > 2) {
                                    const results =
                                        clinicianExperienceLevels.levels.filter(
                                            (l) =>
                                                l.value
                                                    .toLowerCase()
                                                    .includes(
                                                        searchLabel.toLowerCase()
                                                    ) ||
                                                l.category
                                                    .toLowerCase()
                                                    .includes(
                                                        searchLabel.toLowerCase()
                                                    )
                                        );
                                    if (results.length > 0) {
                                        setFilteredLevels(results);
                                    } else {
                                        setFilteredLevels(
                                            clinicianExperienceLevels.levels
                                        );
                                    }
                                } else {
                                    setFilteredLevels(
                                        clinicianExperienceLevels.levels
                                    );
                                }
                            }}
                        />
                        <Button
                            onClick={() => setShowAddExperienceLevelDialog(true)}
                        >
                            Add Experience Level
                        </Button>
                    </div>

                    {userAcl?.super_admin && (
                        <div className={styles.headerBottom}>
                            <Button
                                variant="secondary"
                                onClick={() => setShowNotifyDialog(true)}
                            >
                                Send update reminder to all clinicians
                            </Button>
                        </div>
                    )}
                </div>
                <div className={styles.experienceLevelsContainer}>
                    {filteredLevels.map((l, idx) => {
                        if (!categorySet.has(l.category)) {
                            categorySet.add(l.category);
                            categoryColors[l.category] = COLORS[idx];
                        }
                        const exp_level_clincian_count = (
                            clinicianExperienceLevels.clinician_levels[l.value] || []
                        ).length;
                        const exp_level_clinician_locations = Array.from(
                            new Set(
                                [].concat.apply(
                                    [],
                                    (
                                        clinicianExperienceLevels.clinician_levels[
                                            l.value
                                        ] || []
                                    ).map((cl) => cl.locations) as any
                                )
                            )
                        );
                        return (
                            <div
                                key={`filtered-level-${idx}-${l.category}`}
                                className={styles.experienceLevelCard}
                            >
                                <div className={styles.experienceLevelCardHeader}>
                                    <div className={styles.experienceLevelValue}>
                                        {l.value}
                                        <Badge
                                            content={l.category}
                                            textColor="var(--color-white)"
                                            bgColor={
                                                categoryColors[l.category] ||
                                                "#6c757d"
                                            }
                                        />
                                    </div>
                                    <Dropdown>
                                        <Dropdown.Trigger>
                                            <MoreIcon />
                                        </Dropdown.Trigger>
                                        <Dropdown.Content align="end">
                                            <Dropdown.Item
                                                onClick={() => {
                                                    setShowDeleteDialog(true);
                                                    setSelectedExperienceLevel(l);
                                                }}
                                            >
                                                Delete
                                            </Dropdown.Item>
                                        </Dropdown.Content>
                                    </Dropdown>
                                </div>
                                <div className={styles.experienceLevelContent}>
                                    <span>
                                        {l.type === ExperienceLevelType.LOWHIGH
                                            ? " Low to High"
                                            : l.type ===
                                              ExperienceLevelType.CONVFLUENT
                                            ? "Conversational or Fluent"
                                            : " Yes or No"}
                                    </span>
                                    <span>
                                        {exp_level_clincian_count} clinicians
                                    </span>
                                    <Accordion
                                        title={`Locations (${exp_level_clinician_locations.length})`}
                                        className={styles.locationsAccordion}
                                        titleClassName={
                                            styles.locationsAccordionTitle
                                        }
                                        contentClassName={
                                            styles.locationsAccordionContent
                                        }
                                        defaultOpen={true}
                                        iconColor="#576A81"
                                    >
                                        {exp_level_clinician_locations
                                            .sort()
                                            .join(", ")}
                                    </Accordion>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
